import { useState } from "react";
import { JobService } from "@/services/jobService";

/**
 * Sends a job description file and puts the extracted text on the AI-modal
 * state, with the real upload progress beside it.
 *
 * The percentage comes from axios' `onUploadProgress` — the bytes actually on
 * the wire. It used to be a `setInterval` counting to 100% in 2 seconds, which
 * the request was then made to *wait for* (`Promise.all([upload, simulate()])`),
 * so a 200ms upload always took two seconds and a slow one showed 100% while it
 * was still sending. Once the last byte is up, the server is reading the file:
 * `uploadProgress` sits at 100 and `uploading` stays true, which is what the
 * screen tells the user.
 */
export const useJDUpload = (setData) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);


   const clearError = () => setError(null);
  const uploadJD = async (file) => {
    try {
      setUploading(true);
      setError(null);
      // reset progress
      setData((prev) => ({
        ...prev,
        uploadProgress: 0,
      }));

      const response = await JobService.uploadJD(file, (event) => {
        // `total` is missing when the browser cannot size the body; leaving the
        // last known percentage alone beats dividing by undefined.
        if (!event?.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        setData((prev) => ({ ...prev, uploadProgress: percent }));
      });

      const extracted = response?.extracted_text || response?.text || response?.content || "";

      setData((prev) => ({
        ...prev,
        extractedText: extracted,
      }));
  } catch (err) {
      console.error(err);
      const message = err?.response?.data?.message || err?.message || "Failed to upload file. Please try again.";
      setError(message);

      setData((prev) => ({
        ...prev,
        extractedText: "",
        uploadError: true,
      }));
    } finally {
      setUploading(false);
    }
  };

  return { uploadJD, uploading, error, clearError  };
};
